import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { TextInput } from './TextInput';
import { YesNoInput } from './YesNoInput';
import { MultiSelectInput } from './MultiSelectInput';
import { OkButton } from './OkButton';
export type QuestionType = 'text' | 'email' | 'longtext' | 'yesno' | 'multiselect';
export type QuestionConfig = {
  id: string;
  number: number;
  type: QuestionType;
  question: string;
  helper?: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  single?: boolean;
};
export type QuestionValue = string | boolean | string[] | null;
type QuestionScreenProps = {
  config: QuestionConfig;
  value: QuestionValue;
  onChange: (v: string | boolean | string[]) => void;
  onNext: () => void;
  onBack: () => void;
  canGoBack: boolean;
  error?: string | null;
  isLast?: boolean;
  submitting?: boolean;
  submitError?: string | null;
  honeypot?: string;
  onHoneypotChange?: (v: string) => void;
};
export function QuestionScreen({
  config,
  value,
  onChange,
  onNext,
  onBack,
  canGoBack,
  error,
  isLast,
  submitting,
  submitError,
  honeypot,
  onHoneypotChange
}: QuestionScreenProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  useEffect(() => {
    if (config.type === 'text' || config.type === 'email' || config.type === 'longtext') {
      inputRef.current?.focus();
    }
  }, [config.id, config.type]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === 'Tab') {
        if (canGoBack) {
          e.preventDefault();
          onBack();
        }
        return;
      }
      if (config.type === 'yesno') {
        if (e.key.toLowerCase() === 'y') {
          e.preventDefault();
          onChange(true);
          setTimeout(onNext, 180);
        } else if (e.key.toLowerCase() === 'n') {
          e.preventDefault();
          onChange(false);
          setTimeout(onNext, 180);
        } else if (e.key === 'Enter' && value !== null) {
          e.preventDefault();
          onNext();
        }
      } else if (config.type === 'multiselect') {
        if (e.key === 'Enter') {
          e.preventDefault();
          onNext();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [config.type, value, onChange, onNext, onBack, canGoBack]);
  const isValid = (() => {
    if (!config.required) return true;
    if (config.type === 'yesno') return value !== null;
    if (config.type === 'multiselect') return Array.isArray(value) && value.length > 0;
    return typeof value === 'string' && value.trim().length > 0 && !error;
  })();
  return <motion.div key={config.id} initial={{
    opacity: 0,
    y: 24
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -24
  }} transition={{
    duration: 0.4,
    ease: [0.22, 1, 0.36, 1]
  }} className="min-h-screen w-full flex items-center justify-center px-5 sm:px-6 pt-20 sm:pt-24 pb-12 sm:pb-20" data-id="element-22">
      {canGoBack && <button type="button" onClick={onBack} aria-label="Previous question" className="fixed top-3 left-3 sm:top-5 sm:left-5 z-40 p-2 rounded-md text-neutral-400 hover:text-white hover:bg-white/5 transition-colors" data-id="element-23">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-24" />
        </button>}

      <div className="w-full max-w-2xl" data-id="element-25">
        <div className="flex items-center gap-2 text-neutral-500 text-xs sm:text-sm mb-3 sm:mb-5" data-id="element-26">
          <span className="text-white/80" data-id="element-27">{config.number}</span>
          <span data-id="element-28">→</span>
        </div>

        <h2 className="text-xl sm:text-3xl md:text-4xl font-medium text-white leading-snug tracking-tight" data-id="element-29">
          {config.question}
          {config.required && <span className="text-neutral-600 ml-1" data-id="element-30">*</span>}
        </h2>

        {config.helper && <p className="mt-2 sm:mt-3 text-neutral-500 text-sm sm:text-base" data-id="element-31">
            {config.helper}
          </p>}

        <div className="mt-6 sm:mt-8" data-id="element-32">
          {config.type === 'yesno' ? <YesNoInput value={value as boolean | null} onChange={v => onChange(v)} data-id="element-33" /> : config.type === 'multiselect' ? <MultiSelectInput options={config.options || []} value={value as string[] || []} onChange={v => onChange(v)} single={config.single} data-id="element-34" /> : <TextInput ref={inputRef} value={value as string || ''} onChange={v => onChange(v)} onEnter={() => isValid && onNext()} placeholder={config.placeholder} type={config.type === 'email' ? 'email' : 'text'} multiline={config.type === 'longtext'} error={error} data-id="element-35" />}
        </div>

        {error && config.type !== 'text' && config.type !== 'email' && config.type !== 'longtext' && <p className="text-red-400 text-sm mt-4" data-id="element-36">{error}</p>}

        {isLast && submitError && <p className="text-red-400 text-sm mt-4" role="alert" data-id="element-37">
            {submitError}
          </p>}

        <div className="mt-6 sm:mt-8" data-id="element-38">
          <OkButton onClick={onNext} disabled={!isValid} label={isLast ? 'Submit' : 'OK'} loading={submitting} loadingLabel="Submitting..." data-id="element-39" />
        </div>

        {isLast && <input type="text" name="hp_field" value={honeypot ?? ''} onChange={e => onHoneypotChange?.(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{
        position: 'absolute',
        left: '-9999px',
        width: '1px',
        height: '1px',
        opacity: 0
      }} data-id="element-40" />}
      </div>
    </motion.div>;
}